import React, { Fragment } from "react";
import AdminDataTable from "../../../admin/components/AdminDataTable";
import Card from "../../../../components/ui/Card";
import Button from "../../../../components/ui/Button";
import { PlusCircle, MoreVertical, Edit, Trash2 } from "lucide-react";
import { Menu, Transition } from "@headlessui/react";
import { mockCreator } from "../../../../data/mockData";
import RecentActivityList from "../../../../components/ui/RecentActivityList";

const CommerceTab = ({ products, onEdit, onCreate, onDelete }) => {
  const productColumns = [
    {
      header: "Product",
      key: "product",
      render: (row) => (
        <div className="flex items-center">
          <img
            src={row.imageUrl}
            alt={row.name}
            className="w-10 h-10 rounded-md object-contain bg-slate-100 p-1 mr-4"
          />
          <p className="font-medium text-slate-800">{row.name}</p>
        </div>
      ),
    },
    {
      header: "Price (ETB)",
      key: "price",
      render: (row) => row.priceETB.toLocaleString(),
    },
    {
      header: "Stock",
      key: "stock",
      render: (row) => (
        <span
          className={
            row.stock < 10 ? "text-red-500 font-medium" : "text-slate-600"
          }
        >
          {row.stock}
        </span>
      ),
    },
    { header: "Total Sales", key: "sales", render: (row) => row.sales },
    {
      header: "",
      key: "actions",
      render: (row) => (
        <Menu as="div" className="relative inline-block text-left">
          {/* ... Action Menu JSX remains the same ... */}
          <Menu.Button
            as={Button}
            variant="ghost"
            size="sm"
            className="w-8 h-8 p-0 data-[headlessui-state=open]:bg-slate-100"
          >
            <MoreVertical size={16} />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-32 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
              <div className="px-1 py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => onEdit(row)}
                      className={`${
                        active ? "bg-primary text-white" : "text-gray-900"
                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                    >
                      <Edit className="mr-2 h-4 w-4" /> Edit
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => onDelete(row)}
                      className={`${
                        active ? "bg-red-500 text-white" : "text-gray-900"
                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      ),
    },
  ];

  const totalSales = products.reduce((acc, item) => acc + item.sales, 0);

  // ✨ STEP 2: Filter the transactions for this specific tab
  const commerceTransactions = mockCreator.mockTransactions
    .filter((txn) => txn.type === "Commerce")
    .slice(0, 5); // Get the 5 most recent

  return (
    <div className="space-y-6">
      {/* --- Existing Stat Cards --- */}
      <div className="grid grid-cols-1 @md:grid-cols-3 gap-4">
        <Card>
          <p className="text-sm text-slate-500">Total Units Sold</p>
          <p className="text-2xl font-bold text-slate-800">
            {totalSales.toLocaleString()}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-slate-500">Revenue (All Time)</p>
          <p className="text-2xl font-bold text-slate-800">550,500 ETB</p>
        </Card>
        <Card>
          <p className="text-sm text-slate-500">Active Products</p>
          <p className="text-2xl font-bold text-slate-800">{products.length}</p>
        </Card>
      </div>

      {/* --- Existing Products Table --- */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Your Products</h3>
        <Button variant="primary" onClick={onCreate}>
          <PlusCircle size={16} className="mr-2" /> Add Product
        </Button>
      </div>
      <AdminDataTable columns={productColumns} data={products} />

      {/* ✨ STEP 3: Render the new Recent Activity component ✨ */}
      <RecentActivityList
        title="Recent Commerce Activity"
        transactions={commerceTransactions}
      />
    </div>
  );
};

export default CommerceTab;
